import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { OrderApiResponseStateService } from './order-api-response-state.service';
import { LineAdditionalOfferData, WirelessPDPlusCustomizationData, WirelessPDPlusLine, WirelessPDPlusResponse } from '../../../../../models/order/api-models/order-pd-plus-response.model';
import { AdditionalOfferData, OfferType } from '../../../../../models/order/workflow/additional-offer.model';

@Injectable({ providedIn: 'root' })
export class WirelessProductPlusResponseService {
    private orderApiResponseStateService = inject(OrderApiResponseStateService);


    productResponse = computed(() => this.orderApiResponseStateService.wirelessProductDetailsPlusData());
    // Above property is object with keys as line numbers and values as WirelessPDPlusResponse we need to convert it to array of WirelessPDPlusCustomizationData
    // where each item will have lineNumber and customizations array
    productResponseArray = computed(() => {
        const pdResponse = this.productResponse();
        const customizations: WirelessPDPlusResponse[] = [];
        if (pdResponse) {
            Object.keys(pdResponse).forEach(line => {
                const pdLineResponse = pdResponse[line];
                customizations.push(pdLineResponse);
            });
        }
        return customizations;
    });

    private customizations = signal<WirelessPDPlusCustomizationData[]>([]);

    constructor() {
        effect(() => {
            const pdResponse = this.productResponse();
            if (pdResponse) {
                const customizations: WirelessPDPlusCustomizationData[] = [];
                Object.keys(pdResponse).forEach(line => {
                    const pdLineResponse = pdResponse[line];
                    const pdCustomizations = pdLineResponse?.Components?.Component?.Customizations?.Customization;
                    const pdPlusCust = {
                        lineNumber: line,
                        customizations: pdCustomizations
                    }
                    customizations.push(pdPlusCust);
                });
                this.customizations.set(customizations);
            } else {
                this.customizations.set([]);
            }
        });
    }

    findCartSelectedCustomization(): LineAdditionalOfferData[] {
        const customizations = this.customizations();
        const selectedCustomizations: LineAdditionalOfferData[] = [];
        for (const item of customizations) {
            selectedCustomizations.push({
                lineNumber: parseInt(item.lineNumber.replace('PDPLUS_', '')),
                offers: this.findCartSelectedCustomizationByLine(item.lineNumber)?.offers || []
            });
        }
        return selectedCustomizations;
    }

    findCartSelectedCustomizationByLine(lineNumber: number | string): LineAdditionalOfferData {
        const customizations = this.customizations();
        const selectedCustomizations: LineAdditionalOfferData = {
            lineNumber: typeof lineNumber === 'string' ? parseInt(lineNumber.replace('PDPLUS_', '')) : lineNumber,
            offers: []
        };
        for (const item of customizations) {
            if (item.lineNumber === this.getLineNumber(lineNumber) && item.customizations?.length) {
                for (const cust of item.customizations) {
                    const choices = cust.Choices?.Choice;
                    if (choices?.length) {
                        selectedCustomizations.offers.push(...choices.map(choice => {
                            return {
                                customizationCode: cust.CustomizationCode,
                                choice: choice,
                                type: OfferType.Required
                            } as AdditionalOfferData
                        }));
                    }
                }
            }
        }
        return selectedCustomizations;
    }

    findChoiceBySubGroupName(lineNumber: number | string, subGroupName: string) {
        for (const item of this.customizations()) {
            if (item.lineNumber === this.getLineNumber(lineNumber) && item.customizations?.length) {
                for (const cust of item.customizations) {
                    const found = cust.Choices?.Choice?.find(choice => choice.ChoiceSubGroupName === subGroupName);
                    if (found) return found;
                }
            }
        }
        return null;
    }

    findChoiceByCode(lineNumber: number | string, choiceCode: string) {
        for (const item of this.customizations()) {
            if (item.lineNumber === this.getLineNumber(lineNumber) && item.customizations?.length) {
                for (const cust of item.customizations) {
                    const found = cust.Choices?.Choice?.find(choice => choice.ChoiceCode === choiceCode || choice.ChoiceSubGroupName === choiceCode);
                    if (found) {
                        return { customization: cust, choice: found };
                    }
                }
            }
        }
        return null;
    }

    findCustomizationByCode(lineNumber: number | string, customizationCode: string) {
        for (const item of this.customizations()) {
            if (item.lineNumber === this.getLineNumber(lineNumber)) {
                const found = item.customizations.find(cust => cust.CustomizationCode === customizationCode);
                if (found) return found;
            }
        }

        return null;
    }

    getProductPlusDataByLine(lineNumber: number | string) {
        return this.productResponse()?.[this.getLineNumber(lineNumber)];
    }

    findWirelessLine(responseLineNumber: number | string, lineNumber: number | string): WirelessPDPlusLine | null {
        if (!responseLineNumber) return null;
        const line = this.toLineNumber(lineNumber);
        const wirelessLines = this.getProductPlusDataByLine(responseLineNumber)?.Components?.Component?.WirelessLines?.WirelessLine;
        return wirelessLines?.find(wirelessLine => +wirelessLine.LineNumber === line) || null;
    }

    private toLineNumber(lineNumber: number | string): number {
        return typeof lineNumber === 'string' ? +lineNumber.replace('PDPLUS_', '') : lineNumber;
    }

    private getLineNumber(lineNumber: number | string) {
        if (typeof lineNumber === 'string' && lineNumber.startsWith('PDPLUS_')) {
            return lineNumber;
        }
        return 'PDPLUS_' + lineNumber;
    }
}